import { logger } from '../utils/logger';
import React, { useState, useEffect, useCallback, createContext, useContext, useMemo } from 'react';
import axios from 'axios';

import { BACKEND_URL } from '../config/api';

const NotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  followups: { overdue: [], due_today: [], upcoming: [], total_alerts: 0 },
  refresh: () => {},
  markRead: () => {},
  markAllRead: () => {},
  branding: { name: 'VetNeuroHelp', primary_color: '#4B6651', logo_url: '', clinic_id: '' }
});

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [followups, setFollowups] = useState({ overdue: [], due_today: [], upcoming: [], total_alerts: 0 });
  const [branding, setBranding] = useState({ name: 'VetNeuroHelp', primary_color: '#4B6651', logo_url: '', clinic_id: '' });

  const refresh = useCallback(async () => {
    try {
      const [notifRes, fuRes, brandRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/notifications?limit=10`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/notifications/followups`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/branding`, { withCredentials: true })
      ]);
      setNotifications(notifRes.data.items || []);
      setUnreadCount(notifRes.data.unread_count || 0);
      setFollowups(fuRes.data);
      setBranding(brandRes.data);
    } catch (err) {
      logger.warn('NotificationContext: failed to refresh (user may not be logged in)', err?.message);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, [refresh]);

  const markRead = useCallback(async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/notifications/${id}/read`, {}, { withCredentials: true });
      refresh();
    } catch (err) { logger.warn('markRead failed:', err?.message); }
  }, [refresh]);

  const markAllRead = useCallback(async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/notifications/read-all`, {}, { withCredentials: true });
      refresh();
    } catch (err) { logger.warn('markAllRead failed:', err?.message); }
  }, [refresh]);

  const contextValue = useMemo(() => ({ notifications, unreadCount, followups, refresh, markRead, markAllRead, branding }), [notifications, unreadCount, followups, refresh, markRead, markAllRead, branding]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};
